import axios, { AxiosResponse, Method } from 'axios';

import { getApiErrorMessage } from './getApiErrorMessage';
import { getProperty } from './getProperty';

export type GetAbortSignalFn = () => AbortSignal | undefined;
export type ApiOptions = {
  onUploadProgress?: (progressEvent: any) => void;
  signal?: AbortSignal | undefined;
};

export const callApi = async <T>(
  url: string,
  method: Method = 'get',
  payload: Record<string, any> | undefined = undefined,
  headers: Record<string, string> = {},
  options: ApiOptions = {},
): Promise<AxiosResponse<T>> => {
  try {
    const response: AxiosResponse<T> = await axios({
      url,
      method,
      headers: { ...getDefaultHeaders(), ...headers },
      data: payload,
      ...options,
    }).then((res) => {
      // axios specific
      if ((res as any).isAxiosError || !res?.status) {
        throw res;
      }
      return res;
    });
    return response;
  } catch (ex) {
    throw createApiError(ex);
  }
};

class ApiError extends Error {
  private _payload: any;
  private _message: string;
  private _status: number;

  constructor(status: number, message: string, payload: any) {
    super(message);
    Object.setPrototypeOf(this, ApiError.prototype);
    this._payload = payload;
    this._status = status;
    this._message = message;
  }

  get status() {
    return this._status;
  }
  get message() {
    return this._message;
  }
  get payload() {
    return this._payload;
  }
}

const getDefaultHeaders = () => {
  return {
    'Content-Type': 'application/json',
  };
};

const createApiError = (payload: any) => {
  if (!payload.response) {
    return new ApiError(0, 'Service unavailable', payload);
  }
  const status = getProperty(payload, 'response.status', 500);
  const message = getApiErrorMessage(payload, `Unknown Error with status ${status}`);
  const error = new ApiError(status, message, payload);
  return error;
};
